/* Enhance only the theme's product-option fields. Existing change handlers keep
   owning variant availability, gifts and cart updates. */
;(() => {
  const selector = 'select.custom-cart-select, select[data-gift-variant], select[data-compact-select]'
  let nextId = 0

  class CustomOptionSelect extends HTMLElement {
    connectedCallback() {
      if (this.controller) return
      this.select = this.parentElement.querySelector(selector)
      if (!this.select || !('showPopover' in HTMLElement.prototype)) {
        this.remove()
        return
      }
      this.controller = new AbortController()
      const signal = this.controller.signal
      this.trigger = document.createElement('button')
      this.trigger.type = 'button'
      this.trigger.className = 'custom-option-select__trigger'
      this.trigger.setAttribute('role', 'combobox')
      this.trigger.setAttribute('aria-haspopup', 'listbox')
      this.trigger.setAttribute('aria-expanded', 'false')
      const label = this.select.labels?.[0]
      this.trigger.setAttribute(
        'aria-label',
        this.select.getAttribute('aria-label') ||
          label?.querySelector('span')?.textContent.trim() ||
          label?.textContent.trim() ||
          'Choose an option',
      )
      this.list = document.createElement('div')
      this.list.id = `CustomOptionList-${++nextId}`
      this.list.className = 'custom-option-select__list'
      this.list.setAttribute('popover', 'manual')
      this.list.setAttribute('role', 'listbox')
      this.list.setAttribute('aria-label', this.trigger.getAttribute('aria-label'))
      this.trigger.setAttribute('aria-controls', this.list.id)
      this.append(this.trigger, this.list)
      this.select.dataset.customSelectNative = ''
      this.originalTabIndex = this.select.getAttribute('tabindex')
      this.select.tabIndex = -1
      this.select.setAttribute('aria-hidden', 'true')
      this.sync()

      this.trigger.addEventListener(
        'click',
        (event) => {
          // The gift field is inside a label; do not forward a click to its select.
          event.preventDefault()
          this.isOpen ? this.close() : this.open()
        },
        { signal },
      )
      this.trigger.addEventListener('keydown', (event) => this.onKeyDown(event), { signal })
      this.trigger.addEventListener(
        'keyup',
        (event) => {
          if (event.key === 'Escape' && this.handledEscape) {
            event.stopPropagation()
            this.handledEscape = false
          }
        },
        { signal },
      )
      this.select.addEventListener('change', () => this.sync(), { signal })
      this.list.addEventListener('pointerdown', (event) => event.preventDefault(), { signal })
      this.list.addEventListener(
        'click',
        (event) => {
          event.preventDefault()
          const option = event.target.closest('[data-select-index]')
          if (option) this.choose(Number(option.dataset.selectIndex))
        },
        { signal },
      )
      document.addEventListener(
        'pointerdown',
        (event) => {
          if (this.isOpen && !this.contains(event.target)) this.close()
        },
        { signal, capture: true },
      )
      document.addEventListener(
        'focusin',
        (event) => {
          if (this.isOpen && !this.contains(event.target)) this.close()
        },
        { signal },
      )
      window.addEventListener('resize', () => this.close(), { signal })
      document.addEventListener(
        'scroll',
        (event) => {
          if (this.isOpen && !this.list.contains(event.target)) this.close()
        },
        { signal, capture: true },
      )
      this.observer = new MutationObserver(() => {
        this.sync()
        if (this.isOpen) this.close()
      })
      this.observer.observe(this.select, { childList: true, subtree: true, attributes: true, characterData: true })
    }

    disconnectedCallback() {
      this.close()
      this.controller?.abort()
      this.observer?.disconnect()
      this.controller = null
      if (this.select) {
        delete this.select.dataset.customSelectNative
        this.select.removeAttribute('aria-hidden')
        if (this.originalTabIndex === null) this.select.removeAttribute('tabindex')
        else if (this.originalTabIndex !== undefined) this.select.setAttribute('tabindex', this.originalTabIndex)
      }
      this.replaceChildren()
    }

    sync() {
      if (!this.trigger) return
      const option = this.select.options[this.select.selectedIndex]
      const soldOut = Boolean(option && (option.disabled || option.dataset.available === 'false'))
      this.trigger.textContent = option?.textContent.trim() || ''
      this.trigger.dataset.soldOut = String(soldOut)
      this.trigger.disabled = this.select.disabled
      const field = getComputedStyle(this.parentElement)
      const text = getComputedStyle(this.select)
      this.style.padding = field.padding
      this.style.borderRadius = field.borderRadius
      this.trigger.style.font = text.font
      this.trigger.style.color = soldOut ? '' : text.color
    }

    open() {
      if (this.select.disabled) return
      this.sync()
      this.list.replaceChildren()
      this.rows = []
      Array.from(this.select.options).forEach((option, index) => {
        if (option.hidden) return
        const row = document.createElement('div')
        row.id = `${this.list.id}-${index}`
        row.className = 'custom-option-select__option'
        row.dataset.selectIndex = String(index)
        row.setAttribute('role', 'option')
        row.setAttribute('aria-selected', String(index === this.select.selectedIndex))
        row.setAttribute('aria-disabled', String(option.disabled))
        row.dataset.soldOut = String(option.disabled || option.dataset.available === 'false')
        const color = option.dataset.color || option.dataset.swatch
        if (color) {
          const dot = document.createElement('span')
          dot.className = 'custom-option-select__swatch'
          dot.style.background = color
          dot.setAttribute('aria-hidden', 'true')
          row.append(dot)
        }
        row.append(document.createTextNode(option.textContent.trim()))
        this.list.append(row)
        this.rows.push(row)
      })
      if (!this.rows.length) return
      this.list.showPopover()
      this.isOpen = true
      this.trigger.setAttribute('aria-expanded', 'true')
      const compact = this.select.hasAttribute('data-compact-select')
      const rect = compact ? this.parentElement.getBoundingClientRect() : this.getBoundingClientRect()
      const roomBelow = window.innerHeight - rect.bottom - 8
      const roomAbove = rect.top - 8
      const upwards = roomBelow < 180 && roomAbove > roomBelow
      const height = Math.min(260, Math.max(48, upwards ? roomAbove : roomBelow))
      const width = Math.min(window.innerWidth - 16, compact ? rect.width : Math.max(rect.width, 110))
      this.list.style.width = `${width}px`
      this.list.style.maxHeight = `${height}px`
      this.list.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`
      this.list.style.top = upwards ? 'auto' : `${rect.bottom + 4}px`
      this.list.style.bottom = upwards ? `${window.innerHeight - rect.top + 4}px` : 'auto'
      this.activate(this.select.selectedIndex)
    }

    close() {
      if (!this.isOpen) return
      this.list.hidePopover()
      this.isOpen = false
      this.trigger.setAttribute('aria-expanded', 'false')
      this.trigger.removeAttribute('aria-activedescendant')
    }

    activate(index) {
      const row =
        this.rows.find(
          (item) => Number(item.dataset.selectIndex) === index && item.getAttribute('aria-disabled') !== 'true',
        ) || this.rows.find((item) => item.getAttribute('aria-disabled') !== 'true')
      if (!row) return
      this.activeIndex = Number(row.dataset.selectIndex)
      this.rows.forEach((item) => item.toggleAttribute('data-active', item === row))
      this.trigger.setAttribute('aria-activedescendant', row.id)
      // Scroll only the popup; scrollIntoView can also move the PDP or drawer.
      const top = row.offsetTop
      const bottom = top + row.offsetHeight
      if (top < this.list.scrollTop) this.list.scrollTop = top
      else if (bottom > this.list.scrollTop + this.list.clientHeight) {
        this.list.scrollTop = bottom - this.list.clientHeight
      }
    }

    choose(index) {
      const option = this.select.options[index]
      if (!option || option.disabled || option.hidden || this.select.disabled) return
      const changed = this.select.selectedIndex !== index
      this.select.selectedIndex = index
      this.sync()
      this.close()
      this.trigger.focus()
      if (changed) {
        this.select.dispatchEvent(new Event('input', { bubbles: true }))
        this.select.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }

    onKeyDown(event) {
      if (event.key === 'Tab') {
        this.close()
        return
      }
      if (event.key === 'Escape') {
        if (this.isOpen) {
          this.handledEscape = true
          event.preventDefault()
          event.stopPropagation()
          this.close()
        }
        return
      }
      if (['Enter', ' '].includes(event.key)) {
        event.preventDefault()
        this.isOpen ? this.choose(this.activeIndex) : this.open()
        return
      }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault()
        if (!this.isOpen) {
          this.open()
          return
        }
        const choices = this.rows
          .filter((row) => row.getAttribute('aria-disabled') !== 'true')
          .map((row) => Number(row.dataset.selectIndex))
        const position = choices.indexOf(this.activeIndex)
        const index =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? choices.length - 1
              : Math.max(0, Math.min(choices.length - 1, position + (event.key === 'ArrowDown' ? 1 : -1)))
        this.activate(choices[index])
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault()
        if (!this.isOpen) this.open()
        this.search = (Date.now() - (this.searchTime || 0) < 700 ? this.search || '' : '') + event.key.toLowerCase()
        this.searchTime = Date.now()
        const row = this.rows?.find(
          (item) =>
            item.getAttribute('aria-disabled') !== 'true' && item.textContent.toLowerCase().startsWith(this.search),
        )
        if (row) this.activate(Number(row.dataset.selectIndex))
      }
    }
  }

  if (!customElements.get('custom-option-select')) customElements.define('custom-option-select', CustomOptionSelect)
  const enhance = (root) => {
    const selects = [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)]
    selects.forEach((select) => {
      if (!select.parentElement || select.parentElement.querySelector('custom-option-select')) return
      select.parentElement.append(document.createElement('custom-option-select'))
    })
  }
  enhance(document)
  new MutationObserver((records) => {
    records.forEach((record) =>
      record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) enhance(node)
      }),
    )
  }).observe(document.documentElement, { childList: true, subtree: true })
})()
